import { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AdminShell } from "@/components/layout/AdminShell";
import { PlatformGuard } from "@/components/platform/PlatformGuard";
import { PlatformShell } from "@/components/platform/PlatformShell";
import { ImpersonationBanner } from "@/components/platform/ImpersonationBanner";
import { fetchCsrfToken } from "@/services/api-client";
import { LoginPage } from "@/routes/auth/LoginPage";
import { PlatformLogin } from "@/routes/platform/PlatformLogin";
import { DashboardPage } from "@/routes/dashboard/DashboardPage";
import { ProductsPage } from "@/routes/products/ProductsPage";
import { QuizzesPage } from "@/routes/quizzes/QuizzesPage";
import { QuizDetailPage } from "@/routes/quizzes/QuizDetailPage";
import { AnalyticsPage } from "@/routes/analytics/AnalyticsPage";
import { SettingsPage } from "@/routes/settings/SettingsPage";
import { EmbedPage } from "@/routes/embed/EmbedPage";
import { AccountPage } from "@/routes/account/AccountPage";
import { ImportPage } from "@/routes/import/ImportPage";
import { PricingPage } from "@/routes/billing/PricingPage";
import { PlatformDashboard } from "@/routes/platform/PlatformDashboard";
import { PlatformTenants } from "@/routes/platform/PlatformTenants";
import { PlatformTenantDetail } from "@/routes/platform/PlatformTenantDetail";
import { PlatformAuditLogs } from "@/routes/platform/PlatformAuditLogs";
import { SignupClosedPage } from "@/routes/onboarding/SignupClosedPage";
import { VerifyEmailPage } from "@/routes/auth/VerifyEmailPage";
import { ForgotPasswordPage } from "@/routes/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "@/routes/auth/ResetPasswordPage";

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  if (!isAuthenticated) {
    const returnUrl = location.pathname + location.search;
    const loginTarget =
      returnUrl && returnUrl !== "/"
        ? `/login?returnUrl=${encodeURIComponent(returnUrl)}`
        : "/login";
    return <Navigate to={loginTarget} replace />;
  }
  return <>{children}</>;
}

export default function App() {
  useEffect(() => {
    fetchCsrfToken();
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <div className="flex h-screen flex-col">
          {/* Impersonation banner — global mount, visible across tenant- and platform-shell while active */}
          <ImpersonationBanner />
          <Routes>
            {/* Onboarding (public, no auth guard, no shell) — self-signup
                closed (LEGAL 29.10.): static notice, wizard parked */}
            <Route path="/onboarding" element={<SignupClosedPage />} />

            {/* Email verification (public, no auth guard) */}
            <Route path="/verify-email" element={<VerifyEmailPage />} />

            {/* Self-service password reset (public, no auth guard) */}
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Tenant-Admin auth */}
            <Route path="/login" element={<LoginPage />} />

            {/* Tenant-Admin protected routes */}
            <Route
              element={
                <ProtectedRoute>
                  <AdminShell />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/quizzes" element={<QuizzesPage />} />
              <Route path="/quizzes/:quizId" element={<QuizDetailPage />} />
              <Route path="/analytics" element={<AnalyticsPage />} />
              <Route path="/embed" element={<EmbedPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/import" element={<ImportPage />} />
              <Route path="/billing" element={<PricingPage />} />
            </Route>

            {/* Platform-Admin auth */}
            <Route path="/platform/login" element={<PlatformLogin />} />

            {/* Platform-Admin protected routes */}
            <Route path="/platform" element={<PlatformGuard />}>
              <Route element={<PlatformShell />}>
                <Route path="dashboard" element={<PlatformDashboard />} />
                <Route path="tenants" element={<PlatformTenants />} />
                <Route path="tenants/:id" element={<PlatformTenantDetail />} />
                <Route path="audit-logs" element={<PlatformAuditLogs />} />
                <Route index element={<Navigate to="dashboard" replace />} />
              </Route>
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
